import type { CSSProperties } from "react";
import {
  defineLabels,
  firstError,
  resolveLabels,
  unwrap,
  Widget,
  WidgetSkeleton,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type {
  WidgetDataRequirements,
  WidgetManifest,
  WidgetModule,
  WidgetProps,
} from "@nexus/widget-sdk";
import { EmptyDataState, motionClass, useWidgetMotion } from "@nexus/widget-kit";
import { RANKED_SHARES_CONTRACT, type RankedShareList, type RankedSharesData } from "./types";

export type { RankedShareItem, RankedShareList, RankedSharesData } from "./types";
export { RANKED_SHARES_CONTRACT } from "./types";

export interface IpBlockingTopHostsConfig {
  /** Heading. Defaults to "Top Detected Hosts". */
  title?: string;
  /** The list shown (a channel: `iptv`, `web`). Defaults to the first one. */
  listId?: string;
  /** Armada endpoint owners (default) or Hermes ticket IP owners. */
  hostSource?: "armada" | "hermes";
  percentagePrecision?: "auto" | 0 | 1;
  /** Each host's unique IPs or domains under its bar. */
  showCounts?: boolean;
  /** Counts on a line under the bar (default) or between the host and its share. */
  countPlacement?: "below" | "inline";
  labels?: Record<string, string>;
}

export const LABELS = defineLabels([
  { key: "heading", name: "Default heading", default: "Top Detected Hosts" },
  { key: "unit", name: "Unit after the total", default: "targets" },
  { key: "emptyTitle", name: "Empty title", default: "No host data" },
  {
    key: "emptyDescription",
    name: "Empty description",
    default: "No detected target could be attributed to a host for this period.",
  },
] as const);

export const manifest: WidgetManifest = {
  id: "top-detected-hosts",
  name: "Top Detected Hosts",
  description: "The hosting providers serving most detected IPs or domains across platforms.",
  version: "1.0.0",
  labels: LABELS,
  dataRequests: {
    lists: {
      source: "ip-blocking-combined-hosts",
      contract: RANKED_SHARES_CONTRACT,
      configurable: true,
      sources: ["ip-blocking-combined-hosts", "domain-blocking-combined-hosts"],
    },
  },
};

export const defaultConfig: IpBlockingTopHostsConfig = {};

export function getDataRequirements(
  config: IpBlockingTopHostsConfig,
): Record<string, WidgetDataRequirements> {
  return {
    lists: { mode: "select", fields: [config.hostSource === "hermes" ? "hermes" : "armada"] },
  };
}

const count = new Intl.NumberFormat("en");

/** "45%" for a large share, "3.2%" below 10 % so small hosts do not all read "3%". */
export function formatShare(share: number, precision?: "auto" | 0 | 1): string {
  const percent = share * 100;
  if (typeof precision === "number")
    return `${new Intl.NumberFormat("en", { minimumFractionDigits: precision, maximumFractionDigits: precision }).format(percent)}%`;
  return `${new Intl.NumberFormat("en", { maximumFractionDigits: percent < 10 ? 1 : 0 }).format(percent)}%`;
}

export function selectList(
  data: RankedSharesData | null | undefined,
  listId: string | undefined,
): RankedShareList | undefined {
  const lists = data?.lists ?? [];
  return listId
    ? (lists.find((list) => list.id === listId) ??
        (lists.length === 1 && lists[0].id === "all" ? lists[0] : undefined))
    : lists[0];
}

export function Component({ data, config }: WidgetProps<IpBlockingTopHostsConfig>) {
  const labels = resolveLabels(LABELS, config);
  const error = firstError(data, ["lists"]);
  const result = unwrap<RankedSharesData>(data.lists);
  const list = selectList(result, config.listId?.trim() || undefined);
  const loading = Boolean(data.lists?.isLoading && !result);
  const motion = useWidgetMotion();
  const heading = config.title?.trim() || labels.heading;
  const targets = list?.total?.unit === "Domain" ? "domains" : "IPs";
  const total = list?.total ? (
    <span
      className="rounded-full bg-surface-elevated px-2.5 py-0.5 text-metric-detail font-semibold tabular-nums text-secondary"
      title={
        list.coverage
          ? `${count.format(list.coverage.unattributed)} unattributed ${targets}; percentages use all ${count.format(list.total.value)} detected ${targets}`
          : undefined
      }
    >
      {count.format(list.total.value)} {list.total.unit || labels.unit}
    </span>
  ) : null;
  const widest = Math.max(...(list?.items.map((item) => item.share) ?? [0]), 0.0001);
  // Shares are host counts over the list total, so the count is exact once rounded.
  const listTotal = config.showCounts === true ? (list?.total?.value ?? null) : null;
  const inline = listTotal != null && config.countPlacement === "inline";
  const hostCount = (share: number) =>
    listTotal == null ? null : `${count.format(Math.round(share * listTotal))} ${targets}`;

  return (
    <Widget
      title={heading}
      actions={total}
      intrinsicHeight
      stateMinHeight="var(--widget-content-state-min-height)"
    >
      {loading ? (
        <WidgetSkeleton shape="list" />
      ) : error ? (
        <WidgetIssueState error={error} />
      ) : !list || list.items.length === 0 ? (
        <EmptyDataState title={labels.emptyTitle} description={labels.emptyDescription} />
      ) : (
        <ol
          className="density-metric-padding flex min-w-0 flex-col gap-2 wall-short:gap-1"
          aria-label={heading}
        >
          {list.items.map((item, index) => {
            const leader = index === 0;
            return (
              <li
                key={item.label}
                className={`${motionClass(motion, "nx-rise")} grid min-w-0 ${inline ? "grid-cols-[1.5rem_minmax(0,1fr)_auto_4rem]" : "grid-cols-[1.5rem_minmax(0,1fr)_4rem]"} items-baseline gap-x-2 gap-y-1 wall-short:gap-y-0.5`}
                style={{ "--nx-delay": `${index * 60}ms` } as CSSProperties}
              >
                <span
                  aria-hidden
                  className={`text-metric-detail font-bold tabular-nums ${leader ? "text-primary" : "text-muted"}`}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span
                  className={`truncate text-metric-label text-primary ${leader ? "font-semibold" : ""}`}
                  title={item.label}
                >
                  {item.label}
                </span>
                {inline && (
                  <span className="text-right text-metric-detail tabular-nums text-secondary">
                    {hostCount(item.share)}
                  </span>
                )}
                <span
                  className={`text-right text-metric-label tabular-nums text-primary ${leader ? "font-bold" : "font-semibold"}`}
                >
                  {formatShare(item.share, config.percentagePrecision)}
                </span>
                {/* Bars compare hosts against the leader; the figure carries the share itself. */}
                <span
                  aria-hidden
                  className={`${inline ? "col-span-3" : "col-span-2"} col-start-2 block h-1.5`}
                >
                  <span
                    className={`block h-full rounded-full ${leader ? "bg-data-detection" : "bg-data-detection/45"}`}
                    style={{ width: `max(4px, ${(item.share / widest) * 100}%)` }}
                  />
                </span>
                {listTotal != null && !inline && (
                  <span className="col-span-2 col-start-2 text-metric-detail tabular-nums text-secondary">
                    {hostCount(item.share)}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<IpBlockingTopHostsConfig> = {
  manifest,
  Component,
  defaultConfig,
  getDataRequirements,
};
export default widgetModule;
